
import React from 'react';
import { Contact } from '@/hooks/useContacts';
import { useBrands } from '@/hooks/useBrands';
import { useCampaigns } from '@/hooks/useCampaigns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Mail, Phone, Building, User, Calendar, Tag } from 'lucide-react';

interface ContactViewModalProps {
  contact: Contact | null;
  isOpen: boolean;
  onClose: () => void;
}

const ContactViewModal: React.FC<ContactViewModalProps> = ({ contact, isOpen, onClose }) => {
  const { data: brands = [] } = useBrands();
  const { data: campaigns = [] } = useCampaigns();

  if (!contact) return null;

  const linkedBrands = brands.filter(brand => contact.linked_brand_ids?.includes(brand.id));
  const linkedCampaigns = campaigns.filter(campaign => contact.linked_campaign_ids?.includes(campaign.id));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Contact Details</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium text-gray-900">{contact.name}</div>
                  {contact.designation && (
                    <div className="text-sm text-gray-500">{contact.designation}</div>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800">
                  {contact.email}
                </a>
              </div>

              {contact.phone_number && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <a href={`tel:${contact.phone_number}`} className="text-blue-600 hover:text-blue-800">
                    {contact.phone_number}
                  </a>
                </div>
              )}

              {contact.company_name && (
                <div className="flex items-center space-x-3">
                  <Building className="w-5 h-5 text-gray-400" />
                  <div className="text-gray-900">{contact.company_name}</div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {contact.contact_type && (
                <div className="flex items-center space-x-3">
                  <Tag className="w-5 h-5 text-gray-400" />
                  <Badge variant="secondary">{contact.contact_type}</Badge>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div className="text-sm text-gray-500">
                  Created {new Date(contact.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          {/* Linked Brands */}
          {linkedBrands.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Linked Brands</h3>
              <div className="flex flex-wrap gap-2">
                {linkedBrands.map((brand) => (
                  <Badge key={brand.id} variant="outline" className="text-sm">
                    {brand.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Linked Campaigns */}
          {linkedCampaigns.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Linked Campaigns</h3>
              <div className="flex flex-wrap gap-2">
                {linkedCampaigns.map((campaign) => (
                  <Badge key={campaign.id} variant="outline" className="text-sm">
                    {campaign.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {linkedBrands.length === 0 && linkedCampaigns.length === 0 && (
            <div className="text-center py-4 text-gray-500">
              No linked brands or campaigns
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactViewModal;
